import React from 'react';
import AudioIntroduction from './audio/AudioIntroduction';

const Hero: React.FC = () => {
  return (
    <section className="pt-24 md:pt-32 pb-16 md:pb-24">
      <div className="container mx-auto px-4 md:px-6">
        <div className="flex flex-col md:flex-row items-center gap-12">
          <div className="md:w-1/2 space-y-6">
            <div className="inline-block bg-[#F4B6A1]/20 px-4 py-2 rounded-full">
              <span className="text-[#F4B6A1] font-medium">Your Emotional Companion</span>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-800 leading-tight font-['Poppins']">
              You <span className="text-[#B3D9E6]">speak</span>.  Cora <span className="text-[#C2A4D6]">listens</span>. 
              You <span className="text-[#F4B6A1]">feel heard</span>.
            </h1>
            <p className="text-lg md:text-xl text-gray-600 font-['Merriweather']">
              An AI-powered reflection guide that listens with empathy, responds with understanding, 
              and provides emotional support exactly when you need it most.
            </p>
            
            <AudioIntroduction />

            <div className="flex flex-col sm:flex-row gap-4">
              <button className="bg-[#C2A4D6] hover:bg-[#B390C9] text-white py-3 px-8 rounded-full text-lg font-medium transition-all transform hover:scale-105 shadow-md">
                Try Free For 7 Days
              </button>
              <a 
                href="#how-it-works"
                className="flex items-center justify-center py-3 px-8 rounded-full text-lg font-medium border border-gray-300 hover:border-[#B3D9E6] hover:text-[#B3D9E6] transition-colors"
              >
                Learn How It Works
              </a>
            </div>

            {/* App Store Download Buttons */}
            <div className="mt-4 flex flex-wrap gap-3 items-center justify-center sm:justify-start">
              <a 
                href="https://apps.apple.com/app/feelheard/id6746776325?platform=iphone"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block"
              >
                <img 
                  src="https://developer.apple.com/app-store/marketing/guidelines/images/badge-download-on-the-app-store.svg"
                  alt="Download on the App Store"
                  className="h-[36px] hover:opacity-90 transition-opacity"
                />
              </a>
              <a 
                href="https://play.google.com/store/apps/details?id=me.feelheard"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block"
              >
                <img 
                  src="https://play.google.com/intl/en_us/badges/static/images/badges/en_badge_web_generic.png"
                  alt="Get it on Google Play"
                  className="h-[52px] hover:opacity-90 transition-opacity"
                />
              </a>
            </div>

            <div className="pt-4 flex items-center gap-4">
              <div className="flex -space-x-2">
                <img
                  className="w-10 h-10 rounded-full border-2 border-white"
                  src="https://images.pexels.com/photos/415829/pexels-photo-415829.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1"
                  alt="User"
                />
                <img
                  className="w-10 h-10 rounded-full border-2 border-white"
                  src="https://images.pexels.com/photos/2379004/pexels-photo-2379004.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1"
                  alt="User"
                />
                <img
                  className="w-10 h-10 rounded-full border-2 border-white"
                  src="https://images.pexels.com/photos/614810/pexels-photo-614810.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1"
                  alt="User"
                />
              </div>
              <p className="text-gray-600">
                Join hundreds of people who are <span className="font-semibold">feeling heard</span>.
              </p>
            </div>
          </div>

          <div className="md:w-1/2 relative">
            <div className="absolute -top-4 -left-4 w-24 h-24 bg-[#A9D0A1]/30 rounded-full"></div>
            <div className="absolute -bottom-4 -right-4 w-32 h-32 bg-[#B3D9E6]/30 rounded-full"></div>
            
            <div className="relative bg-white rounded-2xl shadow-xl p-6 md:p-8">
              <div className="flex flex-col gap-4">
                <div className="flex justify-end">
                  <div className="bg-[#F4B6A1]/20 px-3 py-1 rounded-full text-sm text-[#F4B6A1]">
                    Companion
                  </div>
                </div>
                <div className="bg-[#B3D9E6]/10 p-4 rounded-2xl">
                  <p className="text-gray-700">I've been feeling overwhelmed with work lately, and I just can't seem to focus on anything.</p>
                </div>
                
                <div className="flex justify-start">
                  <div className="bg-[#C2A4D6]/20 px-3 py-1 rounded-full text-sm text-[#C2A4D6]">
                    FeelHeard AI
                  </div>
                </div>
                <div className="bg-[#C2A4D6]/10 p-4 rounded-2xl">
                  <p className="text-gray-700">That sounds really challenging. When you're overwhelmed, even simple tasks can feel monumental. What part of work has been most difficult to manage?</p>
                </div>

                <div className="flex justify-end">
                  <div className="bg-[#F4B6A1]/20 px-3 py-1 rounded-full text-sm text-[#F4B6A1]">
                    Companion
                  </div>
                </div>
                <div className="bg-[#B3D9E6]/10 p-4 rounded-2xl">
                  <p className="text-gray-700">I think it's the constant emails and meetings. I don't have time to do actual work.</p>
                </div>
                
                <div className="flex justify-start">
                  <div className="bg-[#C2A4D6]/20 px-3 py-1 rounded-full text-sm text-[#C2A4D6]">
                    FeelHeard AI
                  </div>
                </div>
                <div className="bg-[#C2A4D6]/10 p-4 rounded-2xl">
                  <p className="text-gray-700">That's a common struggle many face. It sounds like your time is being fragmented, making it hard to find focused work periods. Have you been able to set any boundaries around your availability?</p>
                </div>
              </div>

              <div className="mt-6 text-center">
                <p className="text-gray-400 text-sm">Experience a conversation that feels real</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;